import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import Switch from 'react-switch';
import moment from 'moment';
import { sum } from 'ramda';
import PropTypes from 'prop-types';
import querystring from 'query-string';
import { gql } from '@apollo/client';

import { bookingShape, clientShape, historyShape, searchParamsShape, stationsShape, userShape } from 'constants/propTypes';
import { ActionButton, CancelButton } from 'components/ui/Button';
import { defaultClientData } from 'store/client/state';

import { types, passengerTypes } from 'types/passengers';
import { getFareTypeValues } from './getFareTypeValues';
import JourneySelection from './JourneySelection';

import { noPassengers, searchTypes } from './BookingMagnetMultiJourney.constants';
import {
  validateStandardRange,
  validateGroupRange,
  getRdgStations,
  getDefaultStations,
  getFareTypesToDisplay,
  getMostRecentJourneyForBooking,
  isBookingValid
} from './BookingMagnetMultiJourney.utils';

import {
  BoldLabel,
  BookingMagnetContainer,
  HeaderContainer,
  BookingForm,
  ClearButtonContainer,
  SwitchContainer,
  SelectContainer,
  switchColours,
  Header
} from './BookingMagnetMultiJourney.styles';
import { GET_CUSTOMER_DETAILS } from '../../store/queries';

const EMPTY_SEARCH_PARAMS = {
  outboundDate: null,
  inboundDate: null,
  origin: null,
  destination: null,
  returningOrigin: null,
  returningDestination: null,
  adults: 0,
  adults12To15: 0,
  adults16Plus: 0,
  children: 0,
  children4Only: 0,
  children5To11: 0,
  infants: 0,
  youths: 0,
  seniors: 0,
  seatedInfants: 0
};

const BookingMagnetMultiJourney = ({ booking, stations, user, searchParams, client, history, intl }) => {
  const [state, setState] = useState({
    bookingTypeSelected: user.canHaveStandardBookings ? searchTypes.STANDARD : searchTypes.GROUP,
    disabilityType: null,
    ...noPassengers,
    outboundDate: null,
    inboundDate: null,
    multicity: false,
    productFamilies: undefined,
    contractCode: undefined,
    outboundDatePickerErrorMessage: '',
    inboundDatePickerErrorMessage: '',
    passengerRangeError: '',
    isMember: false
  });

  const emptyFormState = {
    currency: booking.currency,
    outboundDatePickerErrorMessage: '',
    inboundDatePickerErrorMessage: '',
    ...EMPTY_SEARCH_PARAMS
  };

  const recentStations = getMostRecentJourneyForBooking(booking.outbound);
  const detaultStations = getDefaultStations(user, stations);

  const startingOriginCode = recentStations.origin || detaultStations.origin;
  const startingDestinationCode = recentStations.destination || detaultStations.destination;

  const updateQueryFields = (newQueryFields) => {
    setState((prev) => ({
      ...prev,
      ...newQueryFields,
      passengerRangeError: ''
    }));
  };

  const selectFareType = ({ value, name }) => {
    const { bookingTypeSelected } = state;

    setState((prev) => ({
      ...prev,
      ...getFareTypeValues(user, value, bookingTypeSelected),
      fareType: { value, name }
    }));
  };

  const setFirstFareType = (bookingType) => {
    const [firstFareType] = getFareTypesToDisplay(user, bookingType);
    if (firstFareType) {
      selectFareType({
        value: firstFareType.code,
        name: firstFareType.name
      });
    }
  };

  useEffect(() => {
    (async () => {
      const { bookingTypeSelected, multicity } = state;

      if (searchParams) {
        const returningDestination = searchParams.returningDestination || (multicity ? startingOriginCode : null);
        const returningOrigin = searchParams.returningOrigin || (multicity ? startingDestinationCode : null);
        const outboundDate = searchParams.outboundDate && moment(searchParams.outboundDate);
        const inboundDate = searchParams.inboundDate && moment(searchParams.inboundDate);
        const destination = searchParams.destination || startingDestinationCode;
        const origin = searchParams.origin || startingOriginCode;

        updateQueryFields({
          ...searchParams,
          inboundDate,
          outboundDate,
          origin,
          destination,
          returningOrigin,
          returningDestination
        });
        setFirstFareType(bookingTypeSelected);
        setState((prev) => ({
          ...prev,
          currency: booking.currency
        }));

        if (searchParams.returningOrigin) {
          setState((prev) => ({
            ...prev,
            multicity: true
          }));
        }

        client.writeQuery({
          query: gql`
            query {
              billingDetails
            }
          `,
          data: { billingDetails: defaultClientData.billingDetails }
        });

        const membershipNumber = new URLSearchParams(history.location.search).get('membershipNumber');
        if (membershipNumber) {
          try {
            const result = await client.query({
              query: GET_CUSTOMER_DETAILS,
              variables: {
                membershipNumber
              }
            });

            const customerInfo = result?.data?.getCustomerDetails;
            if (customerInfo) {
              setState((prev) => ({
                ...prev,
                isMember: true
              }));
            }
          } catch (err) {
            setState((prev) => ({
              ...prev,
              isMember: false
            }));
          }
        }
      }
    })();
  }, []);

  const setDatePickerErrorMessage = (id, message) => {
    setState((prev) => ({
      ...prev,
      [id]: message
    }));
  };

  const clearForm = (e) => {
    const { productFamilies } = user;
    const [firstProductFamily] = productFamilies;
    const { code, name } = firstProductFamily;

    e.preventDefault();

    setState((prev) => ({
      ...prev,
      ...emptyFormState,
      currency: booking.currency,
      disabilityType: null,
      fareType: { value: code, name }
    }));

    // on clear we need to clear cache which we saved in journey search
    client.writeQuery({
      query: gql`
        query {
          searchParams {
            outboundDate
            inboundDate
            origin
            destination
            returningOrigin
            returningDestination
            adults
            adults12To15
            adults16Plus
            children
            children4Only
            children5To11
            infants
            youths
            seniors
            seatedInfants
          }
        }
      `,
      data: {
        searchParams: {
          ...EMPTY_SEARCH_PARAMS,
          __typename: 'SearchParams'
        }
      }
    });
  };

  const swapOriginDestination = () => {
    const { origin, destination } = state;
    setState((prev) => ({
      ...prev,
      origin: destination,
      destination: origin
    }));
  };

  const handleJourneyChange = (queryFields) => {
    const newState = { ...state, ...queryFields };

    const hadRdgBefore = getRdgStations(stations, state).length > 0;
    const hasRdgAfter = getRdgStations(stations, newState).length > 0;
    const resetProps = hadRdgBefore === hasRdgAfter ? {} : noPassengers;

    setState((prev) => ({
      ...prev,
      ...queryFields,
      ...resetProps,
      passengerRangeError: ''
    }));
  };

  const sendSearchQuery = () => {
    const {
      origin,
      destination,
      outboundDate,
      inboundDate,
      adults12To15,
      adults16Plus,
      children4Only,
      children5To11,
      infants,
      adultsWheelchair,
      childrenWheelchair,
      guideDogs,
      wheelchairCompanions,
      nonWheelchairCompanions,
      returningOrigin,
      returningDestination,
      multicity,
      contractCode,
      currency
    } = state;

    const { name: originStationName } = stations.find((s) => s.uic === origin);
    const { name: destinationStationName } = stations.find((s) => s.uic === destination);
    const searchQueryObj = {
      origin,
      destination,
      adults: booking.passengers.filter((p) => p.type === types.ADULT).length,
      adults12To15,
      adults16Plus,
      children: booking.passengers.filter((p) => p.type === types.CHILD).length,
      children4Only,
      children5To11,
      infants,
      youths: booking.passengers.filter((p) => p.type === types.YOUTH).length,
      seniors: booking.passengers.filter((p) => p.type === types.SENIOR).length,
      adultsWheelchair,
      childrenWheelchair,
      guideDogs,
      wheelchairCompanions,
      nonWheelchairCompanions,
      outboundDate: moment(outboundDate).format('YYYY-MM-DD'),
      originStationName,
      destinationStationName
    };

    const returningOriginStationName = stations.find((s) => s.uic === returningOrigin);
    const returningDestinationStationName = stations.find((s) => s.uic === returningDestination);

    if (returningOriginStationName && returningDestinationStationName) {
      searchQueryObj.returningOriginStationName = returningOriginStationName.name;
      searchQueryObj.returningDestinationStationName = returningDestinationStationName.name;
    }

    if (returningOrigin && multicity) {
      searchQueryObj.returningOrigin = returningOrigin;
    }

    if (returningDestination && multicity) {
      searchQueryObj.returningDestination = returningDestination;
    }

    if (inboundDate && inboundDate.isValid()) {
      searchQueryObj.inboundDate = inboundDate.format('YYYY-MM-DD');
    }

    if (contractCode) {
      searchQueryObj.contractCode = contractCode;
    }

    if (booking.productFamilyGroup) {
      searchQueryObj.productFamilies = booking.productFamilyGroup;
    }

    searchQueryObj.currency = currency;

    const currentQuery = querystring.parse(history.location.search);
    const searchQuery = querystring.stringify({ ...currentQuery, ...searchQueryObj });

    const locationState = history.location.state;

    history.push(`/booking/${booking.reference}/journeys?${searchQuery}`, locationState);
  };

  const submitSearch = (e) => {
    e.preventDefault();

    const {
      adults,
      adults12To15,
      adults16Plus,
      children,
      children4Only,
      children5To11,
      infants,
      seatedInfants,
      youths,
      seniors,
      bookingTypeSelected
    } = state;

    const containsRdgStation = getRdgStations(stations, state).length > 0;
    const isStandardBooking = bookingTypeSelected === searchTypes.STANDARD;
    const isGroupBooking = bookingTypeSelected === searchTypes.GROUP;

    let passengerRangeError;

    if (isStandardBooking && containsRdgStation) {
      passengerRangeError = validateStandardRange({
        adults12To15,
        adults16Plus,
        children4Only,
        children5To11
      });
    }

    if (isGroupBooking) {
      passengerRangeError = validateGroupRange({
        adults,
        children,
        infants,
        seatedInfants,
        youths,
        seniors
      });
    }

    if (passengerRangeError) {
      setState((prev) => ({
        ...prev,
        passengerRangeError
      }));
    } else {
      sendSearchQuery();
      setState((prev) => ({
        ...prev,
        ...noPassengers
      }));
    }
  };

  const toggleMulticity = () => {
    const { multicity, origin, destination } = state;
    setState((prev) => ({
      ...prev,
      multicity: !multicity,
      returningOrigin: destination,
      returningDestination: origin
    }));
  };

  const { formatMessage } = intl;

  const {
    adults,
    adults12To15,
    adults16Plus,
    children,
    children4Only,
    children5To11,
    infants,
    seatedInfants,
    youths,
    seniors,
    origin,
    destination,
    outboundDate,
    inboundDate,
    multicity,
    bookingTypeSelected,
    disabilityType,
    wheelchairCompanions,
    nonWheelchairCompanions,
    guideDogs,
    isMember
  } = state;

  const passengers = {
    adults,
    adults12To15,
    adults16Plus,
    children,
    children4Only,
    children5To11,
    infants,
    seatedInfants,
    youths,
    seniors
  };

  const bookingPassengers = booking.passengers.map((p) => ({
    firstName: p.firstName,
    lastName: p.lastName,
    type: formatMessage({ id: `passengerTypes.${passengerTypes[types[p.type]]}` })
  }));

  const hasChangedPassengerFields =
    sum(
      Object.values({
        ...passengers,
        wheelchairCompanions,
        nonWheelchairCompanions,
        guideDogs
      })
    ) > 0;

  const anyFieldNotEmpty = Boolean(
    (origin && origin.length > 0) ||
      (destination && destination.length > 0) ||
      (outboundDate && outboundDate.isValid()) ||
      (inboundDate && inboundDate.isValid()) ||
      hasChangedPassengerFields ||
      Boolean(disabilityType)
  );

  const fareTypesToDisplay = getFareTypesToDisplay(user, bookingTypeSelected, isMember);

  return (
    <BookingMagnetContainer>
      <HeaderContainer>
        <Header>
          <FormattedMessage id="multiJourney.magnet.addAnotherJourney" />
          {anyFieldNotEmpty && (
            <ClearButtonContainer>
              <ActionButton
                onClick={clearForm}
                label={formatMessage({ id: 'bookingMagnet.clearButton' })}
                id="clear-button"
                data-testid="clear-button"
              />
            </ClearButtonContainer>
          )}
        </Header>
      </HeaderContainer>

      <BoldLabel>
        <FormattedMessage id="multiJourney.magnet.passengers" />:
      </BoldLabel>

      <ol>
        {bookingPassengers.map((p) => (
          <li key={`${p.type}_${p.firstName}_${p.lastName}`}>
            {p.firstName} {p.lastName} (<FormattedMessage id={p.type} />)
          </li>
        ))}
      </ol>

      <HeaderContainer>
        <BoldLabel>
          <FormattedMessage id="multiJourney.magnet.fareType" />:
        </BoldLabel>
        &nbsp;
        {fareTypesToDisplay.find((f) => f.code === booking.productFamilyGroup)?.name}
      </HeaderContainer>

      <HeaderContainer>
        <BoldLabel>
          <FormattedMessage id="multiJourney.magnet.currency" />:
        </BoldLabel>
        &nbsp;
        {booking.currency}
      </HeaderContainer>

      <SwitchContainer>
        <small className="switch-label">{formatMessage({ id: 'bookingMagnet.sameStations' })}</small>
        <Switch
          checked={multicity}
          onChange={toggleMulticity}
          onColor={switchColours.onColor}
          offColor={switchColours.offColor}
          offHandleColor={switchColours.offHandleColor}
          onHandleColor={switchColours.onHandleColor}
          uncheckedIcon={false}
          checkedIcon={false}
          width={40}
          height={20}
          id="multicity-toggle"
          data-testid="multicity-toggle"
        />
        <small className="switch-label">{formatMessage({ id: 'bookingMagnet.differentStations' })}</small>
      </SwitchContainer>

      <BookingForm
        id="booking-form"
        onSubmit={submitSearch}
        autoComplete="off"
        avoidContentJumping={bookingTypeSelected === searchTypes.WITH_DISABILITY}
      >
        <JourneySelection
          onJourneyChange={handleJourneyChange}
          swapOriginDestination={swapOriginDestination}
          outboundDatePickerErrorMessage={state.outboundDatePickerErrorMessage}
          inboundDatePickerErrorMessage={state.inboundDatePickerErrorMessage}
          setDatePickerErrorMessage={setDatePickerErrorMessage}
          stations={stations}
          {...state}
        />
        <SelectContainer>
          <div>
            <ActionButton
              disabled={!isBookingValid(state, user)}
              label={formatMessage({ id: 'bookingMagnet.searchTrains' })}
              id="submit-button"
              data-testid="submit-button"
              variant="primary"
            />
          </div>

          <div>
            <CancelButton
              label={formatMessage({ id: 'multiJourney.magnet.cancel' })}
              type="button"
              id="cancel-button"
              data-testid="cancel-button"
              onClick={() => history.push(`/booking/${booking.reference}/review-booking`, history.location.state)}
            />
          </div>
        </SelectContainer>
      </BookingForm>
    </BookingMagnetContainer>
  );
};

BookingMagnetMultiJourney.propTypes = {
  history: historyShape,
  searchParams: searchParamsShape,
  stations: stationsShape,
  client: clientShape,
  intl: PropTypes.shape({ formatMessage: PropTypes.func }),
  user: userShape,
  booking: bookingShape
};

export default BookingMagnetMultiJourney;
